import { calculateFileMetadata } from '~/utils';
import { useDefaultMutation } from '~/lib/react-query';
import { useUploadFileActiveStorage } from './useUploadFileActiveStorage';

export interface CreateMetadataParams {
  fileName: string;
  fileType: string;
  fileChecksum: string;
  fileSize: number;
}

export interface UpdateMetadataParams {
  id: string;
  signedId: string;
  isUploaded: boolean;
}

export interface CreateMetadataResponse {
  id: string;
  uploadMetadata: {
    signedId: string;
    url: string;
    headers: Record<string, string>;
  };
  uploader: {
    id: number;
    name: string;
    avatarUrl: string;
  };
  createdAt: number;
  updatedAt: number;
}

export type MultiStepFileUploaderParams = {
  onCreateMetadata: (metadata: CreateMetadataParams) => Promise<CreateMetadataResponse>;
  onUpdateMetadata: (metadata: UpdateMetadataParams) => Promise<void>;
};

/**
 * Generic Hook for uploading files in a multistep process:
 * 1. Create the file metadata and generate a pre-signed URL
 * 2. Upload the file to S3 directly via the pre-signed URL
 * 3. Update the file metadata with S3 details
 */
export function useMultiStepFileUpload({ onCreateMetadata, onUpdateMetadata }: MultiStepFileUploaderParams) {
  const uploadFileActiveStorage = useUploadFileActiveStorage();

  const uploadFile = async (file: File): Promise<{ id: string }> => {
    const fileMetadata = await calculateFileMetadata(file);

    if (!fileMetadata) {
      throw new Error('Failed to calculate file metadata');
    }

    const data = await onCreateMetadata({
      fileName: fileMetadata.name,
      fileSize: fileMetadata.size,
      fileType: fileMetadata.type,
      fileChecksum: fileMetadata.checksum,
    });

    try {
      await uploadFileActiveStorage.mutateAsync({
        file,
        url: data.uploadMetadata.url,
        headers: data.uploadMetadata.headers,
      });
    } catch (error) {
      await onUpdateMetadata({
        id: data.id,
        signedId: data.uploadMetadata.signedId,
        isUploaded: false,
      });

      throw new Error('Failed to upload file.');
    }

    await onUpdateMetadata({
      id: data.id,
      signedId: data.uploadMetadata.signedId,
      isUploaded: true,
    });

    return { id: data.id };
  };

  return useDefaultMutation(uploadFile);
}
